/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI
 * (C) Copyright IBM Corp. 2018
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import RgbSlider from './RgbSlider';
import ColorConversion from 'utilities/ColorConversion';
import StringResources from 'nls/StringResources';

import {
	TextInput,
	Label
} from 'ca-ui-toolkit';

export default class ColorWheel extends Component{

	static propTypes = {
		onColorChange: PropTypes.func,
		selectedColorType: PropTypes.string,
		color: PropTypes.string
	}

	constructor(props){
		super(props);

		this.redSliderRef = null;
		this.greenSliderRef = null;
		this.blueSliderRef = null;
		this.hueSliderRef = null;
		this.saturationSliderRef = null;
		this.brightnessSliderRef = null;
		this.hexRef = null;
		this.previewRef = null;

		this.colorWheel = null;
		this.normalPickerRef = null;

		this.distanceBetweenPnts = null;
		this.canvasContext = null;

		this.elLeftPnt = null;
		this.elTopPnt = null;
		this.autoLeft = null;
		this.autoTop = null;

		this.pntQuad = 0;

		this.state = {
			mouseDown: false,
			wheelOriginX: 0,
			wheelOriginY: 0,
			selectedColorType: props.selectedColorType || 'RGB',
			hexVal: props.color || '#FFFFFF',
			hslColor: ColorConversion.convertRgbToHsl(props.color || '#FFFFFF')
		};
	}

	componentDidMount() {
		this.addEventsForAllInputs();
		this.updateCanvas();
		this.updateColorPicker();
		this.previewRef.style.backgroundColor = this.props.color;
	}

	componentDidUpdate(prevProps){
		if(this.props.selectedColorType != prevProps.selectedColorType){
			this.setState({
				selectedColorType: this.props.selectedColorType
			});
		}
		if(this.props.color != prevProps.color){
			let newColor = this.props.color || '#FFFFFF';
			this.previewRef.style.backgroundColor = newColor;
			let newState = {
				hexVal: newColor
			};
			// we check if the current HSL would map to a different RGB, and if so we change it
			if( ColorConversion.convertRgbValToRgbHex(ColorConversion.convertHslToRgb(this.state.hslColor)) != newColor ) {
				newState.hslColor = ColorConversion.convertRgbToHsl( newColor );
			}
			this.setState(newState);
		}
	}

	addEventsForAllInputs = () => {
		this.colorWheel.onclick = this.clickOnPalette.bind( this );
	}

	updateCanvas = () =>{
		this.canvasContext = this.colorWheel.getContext('2d');
		// Center coordinates of the color wheel
		const xCoordinate = this.colorWheel.width/2;
		const yCoordinate = this.colorWheel.height/2;
		this.setState({ wheelOriginX: xCoordinate });
		this.setState({ wheelOriginY: yCoordinate });
		//Radius of the color wheel
		const iRadius = this.colorWheel.width/2;
		const bCounterClockwise = false;

		//For loop used to draw the color wheel pixel by pixel on the canvas
		for ( let angle = 0; angle <= 360; angle++) {
			const v_iStartAngle = (angle - 2) * Math.PI/180;
			const v_iEndAngle = angle  * Math.PI/180;
			this.canvasContext.beginPath();
			this.canvasContext.moveTo(xCoordinate, yCoordinate);
			this.canvasContext.arc(xCoordinate, yCoordinate, iRadius, v_iStartAngle, v_iEndAngle, bCounterClockwise);
			this.canvasContext.closePath();
			const v_iGradient = this.canvasContext.createRadialGradient(xCoordinate, yCoordinate, 0, xCoordinate, yCoordinate, iRadius);
			v_iGradient.addColorStop(0, 'hsl(' + angle + ', 10%, 100%)');
			v_iGradient.addColorStop(1, 'hsl(' + angle + ', 100%, 50%)');
			this.canvasContext.fillStyle = v_iGradient;
			this.canvasContext.fill();
		}
	}

	updateColorPicker = () =>{
		this.normalPickerRef.style.top = -((this.colorWheel.height/2) + (this.normalPickerRef.clientHeight/2)) +'px';
		this.normalPickerRef.style.left = ((this.colorWheel.width/2) - (this.normalPickerRef.clientWidth/2)) +'px';
	}

	initPaletteTouchMove = (event) =>{
		event.preventDefault();
		event.stopPropagation();

		this.setState({ mouseDown: true });

		this.normalPickerRef.ontouchmove = this.moveOnPalette.bind( this );
		this.normalPickerRef.ontouchend = this.endPaletteMove.bind( this );
		this.colorWheel.ontouchmove = this.moveOnPalette.bind( this );
		this.colorWheel.ontouchend = this.endPaletteMove.bind( this );
	}

	initPaletteMove = (event) =>{
		event.preventDefault();
		event.stopPropagation();

		this.setState({ mouseDown: true });

		this.normalPickerRef.onmousemove = this.moveOnPalette.bind( this );
		this.normalPickerRef.touchmove = this.moveOnPalette.bind( this );
		this.normalPickerRef.onmouseup = this.endPaletteMove.bind( this );

		this.colorWheel.onmousemove = this.moveOnPalette.bind( this );
		this.colorWheel.touchmove = this.moveOnPalette.bind( this );
		this.colorWheel.onmouseup = this.endPaletteMove.bind( this );
	}

	clickOnPalette = (event) =>{
		event.preventDefault();
		event.stopPropagation();

		this.setState({ mouseDown: true });
		this.moveOnPalette(event);
		this.endPaletteMove(event);
	}

	moveOnPalette = (event) =>{
		if(!this.state.mouseDown) {
			return;
		}

		let eventX = null;
		let eventY = null;

		if(event.type == 'touchmove'){
			eventX = event.targetTouches[0].clientX;
			eventY = event.targetTouches[0].clientY;
		}else{
			eventX = event.clientX;
			eventY = event.clientY;
		}

		const elementRelativePosition = this.getRelativeOffset();
		this.elLeftPnt = eventX - elementRelativePosition.left;
		this.elTopPnt = eventY - elementRelativePosition.top;

		let circleBoundary = this.computePointOnCircle(this.elLeftPnt, this.elTopPnt);

		switch(this.pntQuad){
			case 1:
				this.elLeftPnt = (this.elLeftPnt < circleBoundary.left) ? (circleBoundary.left) : this.elLeftPnt;
				this.elTopPnt = (this.elTopPnt < circleBoundary.top) ? (circleBoundary.top) : this.elTopPnt;
				this.normalPickerRef.style.left = (this.elLeftPnt - (this.normalPickerRef.clientWidth/2)) +'px';
				this.normalPickerRef.style.top = (this.elTopPnt - (this.colorWheel.height + this.normalPickerRef.clientHeight/2)) +'px';
				break;
			case 2:
				this.elLeftPnt = (this.elLeftPnt > circleBoundary.left) ? (circleBoundary.left) : this.elLeftPnt;
				this.elTopPnt = (this.elTopPnt < circleBoundary.top) ? (circleBoundary.top) : this.elTopPnt;
				this.normalPickerRef.style.left = (this.elLeftPnt - (this.normalPickerRef.clientWidth/2)) +'px';
				this.normalPickerRef.style.top = (this.elTopPnt - (this.colorWheel.height + this.normalPickerRef.clientHeight/2)) +'px';
				break;
			case 3:
				this.elLeftPnt = (this.elLeftPnt > circleBoundary.left) ? (circleBoundary.left) : this.elLeftPnt;
				this.elTopPnt = (this.elTopPnt > circleBoundary.top) ? (circleBoundary.top) : this.elTopPnt;
				this.normalPickerRef.style.left = (this.elLeftPnt - (this.normalPickerRef.clientWidth/2)) +'px';
				this.normalPickerRef.style.top = (this.elTopPnt - (this.colorWheel.height + this.normalPickerRef.clientHeight/2)) +'px';
				break;
			case 4:
				this.elLeftPnt = (this.elLeftPnt < circleBoundary.left) ? (circleBoundary.left) : this.elLeftPnt;
				this.elTopPnt = (this.elTopPnt > circleBoundary.top) ? (circleBoundary.top) : this.elTopPnt;
				this.normalPickerRef.style.left = (this.elLeftPnt - (this.normalPickerRef.clientWidth/2)) +'px';
				this.normalPickerRef.style.top = (this.elTopPnt - (this.colorWheel.height + this.normalPickerRef.clientHeight/2)) +'px';
				break;
		}
	}

	getRelativeOffset(){
		const clientRect = this.colorWheel.getBoundingClientRect();
		const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

		const position = { top: (clientRect.top + scrollTop), left: (clientRect.left + scrollLeft) };

		return position;
	}

	endPaletteMove = () =>{
		let rgbVal = {};
		this.setState({ mouseDown: false });
		const dataArray = this.canvasContext.getImageData(this.elLeftPnt, this.elTopPnt, 1, 1).data;
		rgbVal.red = dataArray[0];
		rgbVal.green = dataArray[1];
		rgbVal.blue = dataArray[2];
		let rgbCode = ColorConversion.convertRgbValToRgbHex(rgbVal);

		this.previewRef.style.backgroundColor = rgbCode;
		this.props.onColorChange(rgbCode, false);
	}

	computePointOnCircle(originLeft, originTop){
		let pntRadius = Math.sqrt(Math.pow(Math.abs(this.state.wheelOriginX - originLeft), 2) + Math.pow(Math.abs(this.state.wheelOriginY - originTop), 2));
		let circleRadius = 125;
		let angle = Math.acos(Math.abs(this.state.wheelOriginX - originLeft)/pntRadius);
		let leftNew = 0;
		let topNew = 0;

		if(originLeft > this.state.wheelOriginX && originTop < this.state.wheelOriginY){
			this.pntQuad = 2;
			angle = (180 * (Math.PI/180)) - angle;
		}else if(originLeft > this.state.wheelOriginX && originTop > this.state.wheelOriginY){
			this.pntQuad = 3;
			angle = angle + (180 * (Math.PI/180));
		}else if(originLeft < this.state.wheelOriginX && originTop > this.state.wheelOriginY){
			this.pntQuad = 4;
			angle = (360 * (Math.PI/180)) - angle;
		}else{
			this.pntQuad = 1;
		}

		leftNew = this.state.wheelOriginX - (circleRadius * Math.cos( angle ));
		topNew = this.state.wheelOriginY - (circleRadius * Math.sin( angle ));

		let pointOnCircle = {
			left: leftNew,
			top: topNew
		};

		return pointOnCircle;
	}

	validateTextInput = (value, color) =>{
		if ( isNaN(value) ) {
			return;
		}
		let rgbColor = ColorConversion.convertRgbHexToRgbVal(this.props.color || '#FFFFFF');
		let hslColor = this.state.hslColor;
		let rgbHex = null;

		if(color == 'Red'){
			rgbColor.red = parseInt(value, 10);
		}else if(color == 'Green'){
			rgbColor.green = parseInt(value, 10);
		}else if(color == 'Blue'){
			rgbColor.blue = parseInt(value, 10);
		}else if(color == 'Hue'){
			hslColor.hue = parseInt(value, 10);
		}else if(color == 'Saturation'){
			hslColor.sat = parseInt(value, 10)/100;
		}else if(color == 'Brightness'){
			hslColor.light = parseInt(value, 10)/100;
		}

		if(this.state.selectedColorType == 'RGB'){
			rgbHex = ColorConversion.convertRgbValToRgbHex(rgbColor);
		}else{
			rgbColor = ColorConversion.convertHslToRgb(hslColor);
			rgbHex = ColorConversion.convertRgbValToRgbHex(rgbColor);
		}
		this.setState( { hslColor: hslColor });

		if ( rgbHex != this.props.color ) {
			this.previewRef.style.backgroundColor = rgbHex;
			this.props.onColorChange(rgbHex, false);
		}
	}

	validator = (regex, color) =>{
		let matches = color.match(regex);
		return matches && matches.length == 1 && matches[0] === color;
	}

	validateHexInput = (value) =>{
		// inject the hex string identifier if not present
		if ( value.length == 0 || value[0] != '#' ) {
			value = '#' + value;
		}

		// if any pair is not hex digits, return before updating state to prevent the input
		const regex = /[0-9A-Fa-f]{1,2}/;
		if ( value.length > 1 && !this.validator(regex, value.substr(1,2))) {
			return;
		}
		if ( value.length > 3 && !this.validator(regex, value.substr(3,2))) {
			return;
		}
		if ( value.length > 5 && !this.validator(regex, value.substr(5,2))) {
			return;
		}

		// only "process" the input if it's a full hex string
		if ( value.length == 7 ) {
			this.previewRef.style.backgroundColor = value;
			this.props.onColorChange(value.toUpperCase(), false);
		} else {
			// this doesn't need to be set if we use the props onColorChange because it comes down 
			this.setState({ hexVal: value });
		}

	}

	getSliders(type){
		let rgbVal = ColorConversion.convertRgbHexToRgbVal(this.props.color || (this.state.hexVal.length == 7 ? this.state.hexVal : '#FFFFFF'));
		switch(type){
			case 'RGB':

				return(
					<tbody>
						<tr style={{ 'padding-top':'20px', 'height':'50px' }}>
							<RgbSlider
								ref={(element) => {this.redSliderRef = element;}}
								color={'Red'}
								validateInput={this.validateTextInput.bind(this)}
								selectedColorType={this.state.selectedColorType}
								value={rgbVal.red}
							/>
						</tr>
						<tr style={{ 'padding-top':'20px', 'height':'50px' }}>
							<RgbSlider
								ref={(element) => {this.greenSliderRef = element;}}
								color={'Green'}
								validateInput={this.validateTextInput.bind(this)}
								selectedColorType={this.state.selectedColorType}
								value={rgbVal.green}
							/>
						</tr>
						<tr style={{ 'padding-top':'20px', 'height':'50px' }}>
							<RgbSlider
								ref={(element) => {this.blueSliderRef = element;}}
								color={'Blue'}
								validateInput={this.validateTextInput.bind(this)}
								selectedColorType={this.state.selectedColorType}
								value={rgbVal.blue}
							/>
						</tr>
					</tbody>
				);

			case 'HSB':
				return(
					<tbody>
						<tr style={{ 'padding-top':'20px', 'height':'50px' }}>
							<RgbSlider
								ref={(element) => {this.hueSliderRef = element;}}
								color={'Hue'}
								validateInput={this.validateTextInput.bind(this)}
								selectedColorType={this.state.selectedColorType}
								value={this.state.hslColor.hue}
							/>
						</tr>
						<tr style={{ 'padding-top':'20px', 'height':'50px' }}>
							<RgbSlider
								ref={(element) => {this.saturationSliderRef = element;}}
								color={'Saturation'}
								validateInput={this.validateTextInput.bind(this)}
								selectedColorType={this.state.selectedColorType}
								value={this.state.hslColor.sat >= 0 ? Math.round(this.state.hslColor.sat * 100) : null}
							/>
						</tr>
						<tr style={{ 'padding-top':'20px', 'height':'50px' }}>
							<RgbSlider
								ref={(element) => {this.brightnessSliderRef = element;}}
								color={'Brightness'}
								validateInput={this.validateTextInput.bind(this)}
								selectedColorType={this.state.selectedColorType}
								value={this.state.hslColor.light >= 0 ? Math.round(this.state.hslColor.light * 100) : null}
							/>
						</tr>
					</tbody>
				);

		}
	}

	render(){
		let sliders = this.getSliders(this.state.selectedColorType);

		return(
			<table>
				<tbody>
					<tr>
						<td style={{ width: '50%' }}>
							<div style={{ width:'250px', height:'250px' }} className={'clsWheelContainer '}>
								<canvas
									style={ { position:'relative', float: 'left' } }
									width='250'
									height='250'
									id='colorWheel'
									ref={(element) => {this.colorWheel = element;}}
								/>
								<div
									id='colorPicker'
									style={{ float: 'left' }}
									className={'clsWheelSelector clsWheelSelectorEmpty clsPickerWrapper'}
									ref={(element) => {this.normalPickerRef = element;}}>
								</div>
							</div>
						</td>
						<td style={{ width: '50%', verticalAlign:'top', paddingLeft: '24px' }}>
							<div style={{ width: '250px' }}>
								<div id='idHexWrapper'>
									<table style={{ verticalAlign:'top', width: '100%' }}>
										<tbody>
											<tr>
												<td>
													<div
														id='divPreview'
														className='clsPreviewBox'
														ref={(element) => {this.previewRef = element;}}
													/>
												</td>
												<td style={{ width:'100px', padding:'0 16px', textAlign:'center' }}>
													<Label
														id='idHexCodeLabel'
														type='caption'
														label={StringResources.get('hexCode')}
														htmlFor='idTextRgbCode'
														style={{ fontSize: '1em' }}
														title={StringResources.get('hexCode')}
														alt={StringResources.get('hexCode')}
													/>
												</td>
												<td style={{ float: 'right' }}>
													<TextInput
														id='idTextRgbCode'
														ref={(element) => {this.hexRef = element;}}
														value={this.state.hexVal}
														onChange={(value) => this.validateHexInput(value)}
														style={{ width: '90px' }}
														type='text'
														maxLength='7'
														tabIndex={0}
													/>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
								<div id='idRGBWrapper'>
									<table style={{ verticalAlign:'top' }}>
										{sliders}
									</table>
								</div>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		);
	}
}



// WEBPACK FOOTER //
// ./src/js/dialog/ColorWheel.js